import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '../../components/Layout/AppHeader';
import { BottomNav } from '../../components/BottomNav/BottomNav';
import { useAuth } from '../../context/AuthContext';
import { NEW_FLOW_COUNTRIES } from '../../services/claims';
import {
  getAllProducts,
  getAllFactories,
  getMySelfManagedProducts,
  createSelfManagedProduct,
  searchSuppliers,
  createSelfManagedPlanning,
  type ProductOption,
  type FactoryOption,
  type SelfManagedProductOption,
  type SupplierSuggestion,
} from '../../services/api';
import { isOfflineForUser } from '../../services/networkStatus';
import { invalidateSwApiCache } from '../../services/swCache';
import { clearMonthCalendarCache } from '../Position/MonthCalendarView';
import { SearchableSelect } from '../../components/SearchableSelect/SearchableSelect';
import { AutocompleteInput, type Suggestion } from '../../components/AutocompleteInput/AutocompleteInput';
import { getCurrentAddress, LocationError, type Coords } from '../../services/geolocation';
import { useFeatures } from '../../hooks/useFeatures';
import CalendarPicker from '../../components/CalendarPicker/CalendarPicker';
import { getDateLocale } from '../../i18n';
import './CreatePlanningPage.css';

// Nombre legible del país a partir del ISO-2 (los self-managed que maneja la app).
const COUNTRY_NAMES: Record<string, string> = {
  ES: 'Spain', IT: 'Italy', AR: 'Argentina', PK: 'Pakistan',
  EG: 'Egypt', BG: 'Bulgaria', RO: 'Romania', GR: 'Greece', XX: 'Dummy',
};

function todayISO(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function labelFor(name: string | null, id: number): string {
  return name && name.trim() ? name : `#${id}`;
}

export function CreatePlanningPage() {
  const { t } = useTranslation();
  const { claims } = useAuth();
  const features = useFeatures();
  const navigate = useNavigate();

  // Un controlador crea en SUS países self-managed. Un admin no lleva ese
  // claim, así que se le ofrecen todos los del flujo nuevo — sigue siendo el
  // servidor quien decide si puede, no esta lista.
  const countries = useMemo(
    () => (claims.selfManagedCountries.length > 0
      ? claims.selfManagedCountries
      : (claims.isAdmin ? NEW_FLOW_COUNTRIES : [])),
    [claims.selfManagedCountries, claims.isAdmin]
  );
  const [countryCode, setCountryCode] = useState(countries[0] ?? '');
  const [date, setDate] = useState(todayISO());
  const [showCal, setShowCal] = useState(false);
  const [idProduct, setIdProduct] = useState<number | ''>('');
  const [idFactory, setIdFactory] = useState<number | ''>('');
  // Proveedor en texto libre (self-managed). Convive con idFactory: la FK
  // clásica se sigue enviando para no romper informes ni Android.
  const [supplierName, setSupplierName] = useState('');
  // Ubicación de la carga: texto libre, que puede rellenarse desde el GPS o
  // heredarse del proveedor. Las coordenadas solo se guardan si vinieron del
  // GPS; si el usuario reescribe la dirección a mano, dejan de ser válidas.
  const [locationText, setLocationText] = useState('');
  const [locationCoords, setLocationCoords] = useState<Coords | null>(null);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [totalContainers, setTotalContainers] = useState<number | ''>('');
  const [booking, setBooking] = useState('');

  const [products, setProducts] = useState<ProductOption[]>([]);
  // Catálogo self-managed (tabla propia, filtrado por países del token y ya
  // deduplicado por el servidor). Solo se usa en ese perfil.
  const [smProducts, setSmProducts] = useState<SelfManagedProductOption[]>([]);
  const [factories, setFactories] = useState<FactoryOption[]>([]);
  const [loadingRef, setLoadingRef] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Confirmación de país: solo para self-managed con MÁS DE UN país, donde es
  // fácil crear la planning en el país equivocado por el selector.
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const useSmCatalog = features.selfManagedProductCatalog;

  // Productos del país SELECCIONADO. El catálogo se pide una sola vez con los
  // países de cada producto, así que cambiar el desplegable filtra al instante
  // sin volver a la red. Es imprescindible que coincida con el país elegido: el
  // servidor RECHAZA (403) crear una planning con un producto de otro país.
  //
  // Si un producto llega SIN información de países (respuesta antigua servida
  // por la caché del service worker, por ejemplo), NO se oculta: sin ese dato no
  // podemos saber a qué país pertenece, y es mucho peor dejar el catálogo vacío
  // —la pantalla queda inservible— que mostrar alguno de más, que como mucho el
  // servidor rechazaría con un mensaje claro.
  const visibleSmProducts = useMemo(
    () => (countryCode
      ? smProducts.filter((p) => !p.countryCodes?.length || p.countryCodes.includes(countryCode))
      : smProducts),
    [smProducts, countryCode]
  );

  // Al cambiar de país, si el producto elegido no existe allí se limpia la
  // selección: mejor un campo vacío que enviar algo que va a fallar al guardar.
  useEffect(() => {
    if (!useSmCatalog || idProduct === '') return;
    if (!visibleSmProducts.some((p) => p.idSelfManagedProduct === idProduct)) {
      setIdProduct('');
    }
  }, [useSmCatalog, idProduct, visibleSmProducts]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const byName = <T extends { name: string | null }>(a: T, b: T) =>
          (a.name ?? '').localeCompare(b.name ?? '');
        // El catálogo de producto depende del perfil: el self-managed usa su
        // propia tabla (filtrada por sus países), el clásico la maestra de siempre.
        const [p, f] = await Promise.all([
          useSmCatalog ? getMySelfManagedProducts() : getAllProducts(),
          getAllFactories(),
        ]);
        if (!alive) return;
        if (useSmCatalog) {
          setSmProducts([...((p.data as SelfManagedProductOption[]) ?? [])]
            .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')));
        } else {
          setProducts([...((p.data as ProductOption[]) ?? [])].sort(byName));
        }
        setFactories([...(f.data ?? [])].sort(byName));
      } catch {
        if (alive) setError(t('createPlanning.errorGeneric'));
      } finally {
        if (alive) setLoadingRef(false);
      }
    })();
    return () => { alive = false; };
  }, [t, useSmCatalog]);

  // Sugerencias de proveedor: lo que se escribió antes en los países del
  // usuario. useCallback para no reiniciar el debounce del autocompletado en
  // cada render. Si falla (sin red) devuelve vacío y el campo sigue funcionando
  // como texto libre.
  // Acotadas al país SELECCIONADO (no a todos los del usuario), igual que los
  // productos. Depende de countryCode: al cambiar el desplegable, las
  // sugerencias pasan a ser las de ese país.
  const fetchSupplierSuggestions = useCallback(
    async (query: string): Promise<Suggestion<SupplierSuggestion>[]> => {
      try {
        const res = await searchSuppliers(query, countryCode);
        return (res.data ?? [])
          .filter((s) => !!s.name)
          .map((s) => ({ label: s.name, data: s }));
      } catch {
        return [];
      }
    },
    [countryCode]
  );

  // Al ELEGIR un proveedor ya conocido, heredamos su última dirección. Solo si
  // el campo está vacío: si el usuario ya escribió algo, mandan sus datos.
  const handleSupplierPicked = (s: Suggestion<SupplierSuggestion>) => {
    const last = s.data?.lastAddress;
    if (!last || locationText.trim()) return;
    setLocationText(last);
    setLocationError(null);
    const lat = s.data?.lastLatitude;
    const lng = s.data?.lastLongitude;
    setLocationCoords(
      typeof lat === 'number' && typeof lng === 'number'
        ? { latitude: lat, longitude: lng }
        : null
    );
  };

  // "Mi ubicación": GPS → dirección legible. Si no se puede resolver la
  // dirección, el servicio devuelve las coordenadas formateadas, así el campo
  // nunca se queda vacío tras pulsar el botón.
  const handleUseMyLocation = async () => {
    setLocating(true);
    setLocationError(null);
    try {
      const { text, coords } = await getCurrentAddress(
        getDateLocale()
      );
      setLocationText(text);
      setLocationCoords(coords);
    } catch (e) {
      const reason = e instanceof LocationError ? e.reason : 'unavailable';
      const key = reason === 'denied' ? 'locationDenied'
        : reason === 'timeout' ? 'locationTimeout'
        : reason === 'unsupported' ? 'locationUnsupported'
        : 'locationUnavailable';
      setLocationError(t(`createPlanning.${key}`));
    } finally {
      setLocating(false);
    }
  };

  // Alta de producto desde el propio buscador, cuando el catálogo se queda
  // corto. El servidor no duplica: si el nombre ya existe lo reutiliza.
  const handleCreateProduct = async (name: string): Promise<number | null> => {
    if (!confirm(t('createPlanning.confirmAddProduct', { name }))) return null;
    try {
      const res = await createSelfManagedProduct(name, countryCode);
      const created = res.data;
      setSmProducts((prev) =>
        prev.some((p) => p.idSelfManagedProduct === created.idSelfManagedProduct)
          ? prev
          : [...prev, created].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      );
      return created.idSelfManagedProduct;
    } catch {
      setError(t('createPlanning.errorAddProduct'));
      return null;
    }
  };

  const offline = isOfflineForUser();

  // El booking solo es obligatorio donde se pide (flujo clásico). Para
  // self-managed el campo no se muestra y se envía vacío: la columna
  // Planning.IdBookingPortic es nullable y el portal ya convive con plannings
  // sin booking, así que no hace falta inventar un identificador de relleno.
  // El proveedor sustituye a la fábrica en self-managed: se exige uno u otro,
  // según el perfil, nunca los dos.
  const hasSupplierOrFactory = features.supplierFreeText
    ? supplierName.trim().length > 0
    : idFactory !== '';

  // En self-managed NADA es obligatorio salvo país y fecha, y esos dos siempre
  // tienen valor porque se eligen de un selector: el controlador puede crear la
  // planning con lo que sepa en ese momento y completarla después. Antes de
  // crear se le enumeran los campos que deja en blanco (ver emptyFieldLabels).
  // El flujo clásico conserva sus validaciones de siempre.
  const canSubmit = features.supplierFreeText
    ? (!!countryCode && !!date && !submitting && !offline)
    : (!!countryCode && !!date && idProduct !== '' && hasSupplierOrFactory &&
       totalContainers !== '' && Number(totalContainers) > 0 &&
       (!features.planningBookingRequired || booking.trim().length > 0) &&
       !submitting && !offline);

  // Campos sin rellenar, para avisar antes de crear. El orden es el mismo en el
  // que aparecen en pantalla, para que se reconozcan de un vistazo.
  const emptyFieldLabels: string[] = [];
  if (features.supplierFreeText) {
    if (idProduct === '') emptyFieldLabels.push(t('createPlanning.product'));
    if (supplierName.trim().length === 0) emptyFieldLabels.push(t('createPlanning.supplier'));
    if (features.gpsLocation && locationText.trim().length === 0) emptyFieldLabels.push(t('createPlanning.location'));
    if (totalContainers === '' || Number(totalContainers) <= 0) emptyFieldLabels.push(t('createPlanning.totalContainers'));
    if (features.planningBooking && booking.trim().length === 0) emptyFieldLabels.push(t('createPlanning.booking'));
  }

  // Creación real de la planning. Separada de submit() para poder
  // interponer la confirmación de país en el flujo multi-país.
  const doCreate = async () => {
    const [y, m, d] = date.split('-').map(Number);
    setSubmitting(true);
    try {
      const res = await createSelfManagedPlanning({
        year: y, month: m, day: d,
        // Con catálogo self-managed el producto real viaja en
        // idSelfManagedProduct; el servidor ignora idProduct y pone el
        // placeholder de la maestra clásica para no romper la FK.
        // Sin producto elegido NO se manda el id: un Number('') daría 0, y el
        // servidor lo tomaría por "el producto nº 0", que no existe en ningún
        // país y hacía fallar la creación con un 403.
        idProduct: useSmCatalog || idProduct === '' ? 0 : Number(idProduct),
        ...(useSmCatalog && idProduct !== '' ? { idSelfManagedProduct: Number(idProduct) } : {}),
        // Con proveedor libre no se manda fábrica: el servidor conserva su
        // placeholder para la FK (que es NOT NULL) y guarda el proveedor aparte.
        idFactory: features.supplierFreeText ? 0 : Number(idFactory),
        ...(features.supplierFreeText ? { supplierName: supplierName.trim() } : {}),
        // La ubicación se guarda en la carga y además se aprende asociada al
        // proveedor + país, para proponerla la próxima vez.
        ...(features.gpsLocation && locationText.trim()
          ? {
              locationText: locationText.trim(),
              locationLatitude: locationCoords?.latitude,
              locationLongitude: locationCoords?.longitude,
            }
          : {}),
        totalContainers: Number(totalContainers),
        idBookingPortic: booking.trim(),
        countryCode,
      });
      // Datos frescos: invalidar la caché del SW de ese día + los totales del mes
      // (vista calendario) y la caché en memoria del calendario, para que la nueva
      // planning aparezca al instante tanto en la lista como en el calendario.
      invalidateSwApiCache({ dateKey: `${y}-${m}-${d}` });
      clearMonthCalendarCache();
      // Continúa el flujo en el detalle de la carga recién creada. Pasamos la
      // fecha en el state para que DetailPage pueda recuperar la Position
      // completa del backend (idProduct/idPlanning/idCarrier…): sin ella, el
      // primer detalle se crearía sin esos IDs maestros y el backend lo
      // rechazaría por FK (la lista sí los arrastra; la creación directa no).
      navigate(`/detail/${res.data.idCargo}`, { replace: true, state: { date } });
    } catch {
      setError(t('createPlanning.errorGeneric'));
      setSubmitting(false);
    }
  };

  const submit = () => {
    setError(null);
    if (!canSubmit) { setError(t('createPlanning.errorRequired')); return; }
    // Se pide confirmación por dos motivos distintos, y el mismo diálogo sirve
    // para los dos: dejar campos en blanco (hay que enumerarlos) y, con varios
    // países asignados, asegurar que la planning va al país correcto.
    if (emptyFieldLabels.length > 0 || countries.length > 1) {
      setConfirmOpen(true);
      return;
    }
    void doCreate();
  };

  return (
    <div className="create-planning-page">
      <AppHeader title={t('createPlanning.title')} showBack />

      <div className="create-planning-content">
        {offline && (
          <p className="create-planning-banner">{t('createPlanning.offline')}</p>
        )}

        <div className="create-planning-card">
          {/* País: fijo si solo tiene uno, selector si tiene varios */}
          <label className="cp-field">
            <span className="cp-label">{t('createPlanning.country')}</span>
            {countries.length > 1 ? (
              <select className="cp-input" value={countryCode} onChange={(e) => setCountryCode(e.target.value)}>
                {countries.map((c) => (
                  <option key={c} value={c}>{COUNTRY_NAMES[c] ?? c}</option>
                ))}
              </select>
            ) : (
              <input className="cp-input" value={COUNTRY_NAMES[countryCode] ?? countryCode} disabled />
            )}
          </label>

          <div className="cp-field">
            <span className="cp-label">{t('createPlanning.date')}</span>
            <button type="button" className="cp-input cp-input--trigger" onClick={() => setShowCal(true)}>
              {new Date(date + 'T00:00:00').toLocaleDateString(
                getDateLocale(),
                { day: '2-digit', month: 'long', year: 'numeric' }
              )}
            </button>
          </div>

          {showCal && (
            <div className="cp-cal-overlay" onClick={() => setShowCal(false)}>
              <div className="cp-cal-popup" onClick={(e) => e.stopPropagation()}>
                <CalendarPicker
                  value={date}
                  onChange={(v) => { setDate(v); setShowCal(false); }}
                />
              </div>
            </div>
          )}

          <div className="cp-field">
            <span className="cp-label">{t('createPlanning.product')}</span>
            <SearchableSelect
              // visibleSmProducts (NO smProducts): solo los del país elegido.
              options={useSmCatalog
                ? visibleSmProducts.map((p) => ({
                    value: p.idSelfManagedProduct,
                    label: labelFor(p.name, p.idSelfManagedProduct),
                  }))
                : products.map((p) => ({ value: p.idProduct, label: labelFor(p.name, p.idProduct) }))}
              value={idProduct}
              onChange={setIdProduct}
              placeholder={t('createPlanning.selectProduct')}
              emptyText={useSmCatalog ? t('createPlanning.noProducts') : undefined}
              // Solo el catálogo self-managed admite alta por el controlador.
              onCreate={useSmCatalog ? handleCreateProduct : undefined}
              createLabel={(q) => t('createPlanning.addProduct', { name: q })}
            />
          </div>

          {/* Self-managed: "Proveedor" en TEXTO LIBRE que aprende de lo escrito
              antes. El clásico mantiene su selector de fábrica intacto. */}
          <div className="cp-field">
            <span className="cp-label">
              {t(features.supplierFreeText ? 'createPlanning.supplier' : 'createPlanning.factory')}
            </span>
            {features.supplierFreeText ? (
              <AutocompleteInput
                value={supplierName}
                onChange={setSupplierName}
                fetchSuggestions={fetchSupplierSuggestions}
                onPick={handleSupplierPicked}
                placeholder={t('createPlanning.supplierPlaceholder')}
              />
            ) : (
              <SearchableSelect
                options={factories.map((f) => ({ value: f.idFactory, label: labelFor(f.name, f.idFactory) }))}
                value={idFactory}
                onChange={setIdFactory}
                placeholder={t('createPlanning.selectFactory')}
              />
            )}
          </div>

          {/* Ubicación: contigua al proveedor. El botón rellena el campo desde
              el GPS, pero el texto sigue siendo libre y editable — el usuario
              siempre puede corregir lo que proponga el geocodificador. */}
          {features.gpsLocation && (
            <div className="cp-field">
              <div className="cp-label-row">
                <span className="cp-label">{t('createPlanning.location')}</span>
                <button
                  type="button"
                  className="cp-location-btn"
                  onClick={handleUseMyLocation}
                  disabled={locating}
                >
                  {locating ? (
                    <span className="cp-location-spinner" aria-hidden="true" />
                  ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15" aria-hidden="true">
                      <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0013 3.06V1h-2v2.06A8.994 8.994 0 003.06 11H1v2h2.06A8.994 8.994 0 0011 20.94V23h2v-2.06A8.994 8.994 0 0020.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
                    </svg>
                  )}
                  {locating ? t('createPlanning.locating') : t('createPlanning.myLocation')}
                </button>
              </div>
              <input
                className="cp-input"
                value={locationText}
                onChange={(e) => {
                  setLocationText(e.target.value);
                  // Reescrito a mano → las coordenadas del GPS ya no
                  // corresponden a ese texto, así que se descartan.
                  setLocationCoords(null);
                  setLocationError(null);
                }}
                placeholder={t('createPlanning.locationPlaceholder')}
              />
              {locationError && <span className="cp-location-msg cp-location-msg--error">{locationError}</span>}
              {!locationError && locationCoords?.accuracy != null && (
                <span className="cp-location-msg">
                  {t('createPlanning.locationAccuracy', { meters: Math.round(locationCoords.accuracy) })}
                </span>
              )}
            </div>
          )}

          <label className="cp-field">
            <span className="cp-label">{t('createPlanning.totalContainers')}</span>
            <input
              className="cp-input"
              type="number"
              min={1}
              inputMode="numeric"
              value={totalContainers}
              onChange={(e) => setTotalContainers(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </label>

          {features.planningBooking && (
            <label className="cp-field">
              <span className="cp-label">{t('createPlanning.booking')}</span>
              <input className="cp-input" value={booking} onChange={(e) => setBooking(e.target.value)} />
            </label>
          )}

          {error && <p className="cp-error">{error}</p>}

          <button
            className="cp-submit"
            onClick={submit}
            disabled={!canSubmit}
            style={{ opacity: canSubmit ? 1 : 0.55 }}
          >
            {loadingRef ? t('createPlanning.loading') : submitting ? t('createPlanning.creating') : t('createPlanning.create')}
          </button>
        </div>
      </div>

      {confirmOpen && (
        <div className="cp-confirm-overlay" onClick={() => setConfirmOpen(false)}>
          <div className="cp-confirm-popup" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
            {/* Los campos en blanco se ENUMERAN, no solo se cuentan: saber que
                faltan "dos" no dice cuáles, y el aviso perdería su utilidad. */}
            {emptyFieldLabels.length > 0 && (
              <>
                <p className="cp-confirm-text">
                  {t('createPlanning.confirmEmpty', { count: emptyFieldLabels.length })}
                </p>
                <ul className="cp-confirm-list">
                  {emptyFieldLabels.map((label) => <li key={label}>{label}</li>)}
                </ul>
              </>
            )}
            {countries.length > 1 && (
              <p className="cp-confirm-text">
                {t('createPlanning.confirmCountry', { country: COUNTRY_NAMES[countryCode] ?? countryCode })}
              </p>
            )}
            <div className="cp-confirm-actions">
              <button
                type="button"
                className="cp-confirm-btn cp-confirm-btn--ghost"
                onClick={() => setConfirmOpen(false)}
              >
                {t('createPlanning.confirmCancel')}
              </button>
              <button
                type="button"
                className="cp-confirm-btn cp-confirm-btn--primary"
                onClick={() => { setConfirmOpen(false); void doCreate(); }}
              >
                {t('createPlanning.confirmAccept')}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
